/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { Chip, Tooltip } from '@mui/material';
import { Cancel, Schedule, HourglassEmpty, Block } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  InvitationStatus,
  InvitationStatusBadgeProps,
} from '../../../types/invitation';

/**
 * InvitationStatusBadge component displays the status of an invitation
 * with color coding, icons, and optional tooltips for additional context.
 */
const InvitationStatusBadge = ({
  status,
  expiresAt,
  size = 'medium',
  showTooltip = true,
}: InvitationStatusBadgeProps) => {
  const { t } = useTranslation('studio');

  const getStatusConfig = () => {
    switch (status) {
      case InvitationStatus.PENDING:
        return {
          label: t('invitationStatusBadge.pending'),
          color: 'warning' as const,
          icon: <Schedule fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: expiresAt
            ? t('invitationStatusBadge.tooltips.pending', {
                date: new Date(expiresAt).toLocaleDateString(),
              })
            : t('invitationStatusBadge.tooltips.pendingGeneral'),
        };
      case InvitationStatus.DECLINED:
        return {
          label: t('invitationStatusBadge.declined'),
          color: 'error' as const,
          icon: <Cancel fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: t('invitationStatusBadge.tooltips.declined'),
        };
      case InvitationStatus.EXPIRED:
        return {
          label: t('invitationStatusBadge.expired'),
          color: 'default' as const,
          icon: (
            <HourglassEmpty fontSize={size === 'small' ? 'small' : 'medium'} />
          ),
          tooltip: t('invitationStatusBadge.tooltips.expired'),
        };
      case InvitationStatus.CANCELLED:
        return {
          label: t('invitationStatusBadge.cancelled'),
          color: 'default' as const,
          icon: <Block fontSize={size === 'small' ? 'small' : 'medium'} />,
          tooltip: t('invitationStatusBadge.tooltips.cancelled'),
        };
      default:
        return {
          label: t('invitationStatusBadge.unknown'),
          color: 'default' as const,
          icon: undefined,
          tooltip: t('invitationStatusBadge.tooltips.unknown'),
        };
    }
  };

  const config = getStatusConfig();

  const chipElement = (
    <Chip
      label={config.label}
      color={config.color}
      size={size === 'large' ? 'medium' : 'small'}
      icon={config.icon}
      variant="filled"
      sx={{
        fontWeight: 500,
        ...(size === 'small' && {
          fontSize: '0.75rem',
          height: 24,
        }),
        ...(size === 'large' && {
          fontSize: '0.875rem',
          height: 36,
        }),
      }}
    />
  );

  if (showTooltip && config.tooltip) {
    return (
      <Tooltip title={config.tooltip} arrow>
        {chipElement}
      </Tooltip>
    );
  }

  return chipElement;
};

export default InvitationStatusBadge;
